import {
  useEffect,
  useRef,
  useState,
  UIEvent,
  useCallback,
  useMemo,
} from "react";
import DOMPurify from "dompurify";
import cn from "classnames";
import {
  Overlay,
  Button,
  useMediaQuery,
  Carousel,
} from "@rshbintech.dboul/core-ui";
import { specialOfferApi } from "@rshbintech.dboul/http";
import { Close as CloseIcon } from "@rshbintech.dboul/icons";
import commonStyles from "../styles.scss";
import {
  HEX_COLOR_PATTERN,
  DIRECTION_REGEX,
  DEFAULT_BG_COLOR,
  SPECIAL_OFFER_ANALYTICS_ACTION_NAMES,
  SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES,
  CLICK_EVENT_NAME,
} from "../constants";
import { useImages, useMarkAsRead } from "../hooks";
import {
  AnalyticsCategoryName,
  sendAnalytics,
} from "../../common/utils/send-analytics";
import { capitalizeFirstLetter, getSpecialOfferAnalyticsId } from "../utils";
import { useMicroserviceAvailable } from "../../hooks";
import { MICROSERVICES_NAMES } from "../../common/constants";
import { useSnowPlowAnalytics } from "../../common/snow-plow-analytics";
import { isExternalReference, normalizeData } from "./utils";
import {
  SpecialOfferLandingProps,
  BlockTypeEnum,
  ImageTextBlockTypeEnum,
  BlockType,
  ImageTextBlockSizeEnum,
} from "./types";
import {
  ImageTextBlockHorizontal,
  ImageTextBlockVertical,
} from "./components/image-text-block";
import { TextBlock } from "./components/text-block";
import { TabsBlock } from "./components/tabs-block";
import { StepsBlock } from "./components/steps-block";
import { SCROLL_TOP_RANGE } from "./constants";
import { SliderProgress } from "./components/slider-progress";
import styles from "./styles.scss";

export const SpecialOfferLanding = ({
  onClose,
  isOpened,
  data,
  clientInn,
  dataTestId = "special-offer-landing",
  analyticsPageName,
}: SpecialOfferLandingProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewedSlidesList, setViewedSlidesList] = useState<string[]>([]);

  const { markAsReadCallback } = useMarkAsRead();

  const { trackSelfDescribingEvent } = useSnowPlowAnalytics();

  const isSpecialOffersTemplatesMicroserviceAvailable =
    useMicroserviceAvailable(MICROSERVICES_NAMES.SPECIAL_OFFERS_TEMPLATES);

  const infoBlock: BlockType[] | null = data?.[currentSlide]?.infoblock
    ? JSON.parse(data?.[currentSlide]?.infoblock ?? "")
    : null;

  const specialOfferId = data?.[currentSlide]?.id ?? "";

  const isMobile = useMediaQuery("mobile");

  const fixedHeaderRef = useRef<HTMLDivElement | null>(null);

  const analyticsId = getSpecialOfferAnalyticsId(data[currentSlide]);

  useEffect(() => {
    if (isOpened) {
      sendAnalytics({
        eventCategoryName: AnalyticsCategoryName.SPECIAL_OFFER,
        eventActionName:
          SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_OPEN,
        eventValueText: analyticsId,
        inn: clientInn,
      });
    }
  }, [isOpened]);

  useEffect(() => {
    if (isOpened && data[currentSlide]?.id) {
      sendAnalytics({
        eventCategoryName: AnalyticsCategoryName.SPECIAL_OFFER,
        eventActionName:
          SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_ITEM_SHOW,
        eventValueText: analyticsId,
        inn: clientInn,
      });
    }
  }, [isOpened, data[currentSlide]?.id]);

  useEffect(() => {
    const gradientHexColors =
      data?.[currentSlide]?.background?.match(HEX_COLOR_PATTERN);

    const gradientDirection =
      data?.[currentSlide]?.background?.match(DIRECTION_REGEX);

    const isDefaultBackground = !(
      gradientDirection?.[1] &&
      gradientHexColors?.[0] &&
      gradientHexColors?.[1]
    );

    if (!isDefaultBackground) {
      // передаем градиент с сервера в стили компоненту из ui kit через css переменные
      document.documentElement.style.setProperty(
        "--special-offer-landing-bg-gradient",
        `linear-gradient(${Number(gradientDirection?.[1]) - 90}deg, ${
          gradientHexColors?.[0]
        } 2.17%, ${gradientHexColors?.[1]} 90.76%)`
      );
    }

    // Задаем фон для хедера, появляющегося при скролле вниз
    const fixedHeaderBgColor =
      gradientHexColors?.[1] ||
      data?.[currentSlide]?.background ||
      DEFAULT_BG_COLOR;

    document.documentElement.style.setProperty(
      "--special-offer-landing-fixed-header-bg-color",
      fixedHeaderBgColor
    );
  }, [data?.[currentSlide]?.background]);

  const buttonProps = {
    target: "_blank",
    rel: "noopener noreferrer",
  };

  const handleSpecialOfferClose = () => {
    sendAnalytics({
      eventCategoryName: AnalyticsCategoryName.SPECIAL_OFFER,
      eventActionName:
        SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_CLOSE,
      eventValueText: analyticsId,
      inn: clientInn,
    });

    onClose([specialOfferId]);
    setCurrentSlide(0);
    markAsReadCallback(viewedSlidesList);
  };

  const handleChangeSlide = (slideIndex?: number | undefined) => {
    sendAnalytics({
      eventCategoryName: AnalyticsCategoryName.SPECIAL_OFFER,
      eventActionName:
        slideIndex ?? 0 - currentSlide > 0
          ? SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_RIGHT_BUTTON_CLICK
          : SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_LEFT_BUTTON_CLICK,
      inn: clientInn,
    });

    analyticsPageName &&
      isSpecialOffersTemplatesMicroserviceAvailable &&
      trackSelfDescribingEvent({
        eventCategory: `dbo${capitalizeFirstLetter(analyticsPageName)}`,
        eventAction:
          slideIndex ?? 0 - currentSlide > 0
            ? `${analyticsPageName}${SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_RIGHT_BUTTON_CLICK}`
            : `${analyticsPageName}${SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_LEFT_BUTTON_CLICK}`,
        eventValueText: specialOfferId,
      });

    setCurrentSlide(slideIndex ?? 0);
  };

  const handleLandingButtonClick = () => {
    sendAnalytics({
      eventCategoryName: AnalyticsCategoryName.SPECIAL_OFFER,
      eventActionName:
        SPECIAL_OFFER_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_ITEM_BUTTON_CLICK,
      eventValueText: analyticsId,
      inn: clientInn,
    });
  };

  useEffect(() => {
    const currentSlideId = data?.[currentSlide]?.id;

    if (currentSlideId && !viewedSlidesList.includes(currentSlideId)) {
      setViewedSlidesList([...viewedSlidesList, currentSlideId]);
    }
  }, [data, currentSlide, viewedSlidesList]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (isMobile) {
        return;
      }

      const updatedOpacity = normalizeData(
        event.currentTarget.scrollTop,
        SCROLL_TOP_RANGE
      );

      if (
        fixedHeaderRef.current !== null &&
        fixedHeaderRef.current.style.opacity !== updatedOpacity
      ) {
        fixedHeaderRef.current.style.opacity = updatedOpacity;
      }
    },
    [isMobile]
  );

  const landingImages = useMemo(
    () =>
      data?.map((landingSettings) =>
        landingSettings?.imageId
          ? `${specialOfferApi.baseUrl}/api/special-offers/${
              isSpecialOffersTemplatesMicroserviceAvailable ? "v2" : "v1"
            }/external/special-offers/image/${landingSettings?.imageId}`
          : undefined
      ),
    [data, isSpecialOffersTemplatesMicroserviceAvailable]
  );

  const landingImagesSrc = useImages(landingImages);

  const closeButtonAnalyticsProps = useMemo(
    () =>
      analyticsPageName && isSpecialOffersTemplatesMicroserviceAvailable
        ? {
            "data-rptracker-event": CLICK_EVENT_NAME,
            "data-rptracker-category": `dbo${capitalizeFirstLetter(
              analyticsPageName
            )}`,
            "data-rptracker-action": `${analyticsPageName}${SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_CLOSE}`,
            "data-rptracker-value": specialOfferId,
          }
        : {},
    [
      isSpecialOffersTemplatesMicroserviceAvailable,
      specialOfferId,
      analyticsPageName,
    ]
  );

  const buttonAnalyticsProps = useMemo(
    () =>
      analyticsPageName && isSpecialOffersTemplatesMicroserviceAvailable
        ? {
            "data-rptracker-event": CLICK_EVENT_NAME,
            "data-rptracker-category": `dbo${capitalizeFirstLetter(
              analyticsPageName
            )}`,
            "data-rptracker-action": `${analyticsPageName}${SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_ITEM_BUTTON_CLICK}`,
            "data-rptracker-value": specialOfferId,
          }
        : {},
    [
      isSpecialOffersTemplatesMicroserviceAvailable,
      specialOfferId,
      analyticsPageName,
    ]
  );

  const slideAnalyticsProps = useMemo(
    () =>
      analyticsPageName && isSpecialOffersTemplatesMicroserviceAvailable
        ? {
            "data-rptracker-category": `dbo${capitalizeFirstLetter(
              analyticsPageName
            )}`,
            "data-rptracker-action": `${analyticsPageName}${SPECIAL_OFFER_SNOWPLOW_ANALYTICS_ACTION_NAMES.SPECIAL_OFFER_LANDING_CAROUSEL_ITEM_SHOW}`,
            "data-rptracker-value": specialOfferId,
          }
        : {},
    [
      isSpecialOffersTemplatesMicroserviceAvailable,
      specialOfferId,
      analyticsPageName,
    ]
  );

  return (
    <Overlay
      opened={isOpened}
      onClose={handleSpecialOfferClose}
      className={styles.container}
      dataTestId={`${dataTestId}-overlay`}
      fixed
      shouldCloseByEscapePress
    >
      {!isMobile && !!landingImagesSrc[currentSlide] && (
        <img
          alt={data?.[currentSlide]?.imageName}
          className={styles["bg-img"]}
          src={landingImagesSrc[currentSlide]}
        />
      )}
      <div className={styles["visible-content"]} onScroll={handleScroll}>
        {data?.length > 1 && (
          <SliderProgress
            size={data?.length}
            activeIndex={currentSlide}
            onChange={setCurrentSlide}
            className={styles["slider-progress"]}
          />
        )}

        {!isMobile && (
          <div className={styles["content-fixed-header"]} ref={fixedHeaderRef}>
            <p
              className={commonStyles["size-medium-long"]}
              style={{
                color: data[currentSlide]?.textColor,
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data[currentSlide]?.title ?? ""),
              }}
            />
            {!!data[currentSlide]?.buttonText?.length && (
              <a
                href={data[currentSlide]?.buttonLink}
                className={cn(
                  styles["content-header-button"],
                  styles["content-fixed-header-button"]
                )}
                style={{
                  color: data[currentSlide]?.buttonTextColor,
                  backgroundColor: data[currentSlide]?.buttonColor,
                }}
                {...(isExternalReference(data[currentSlide]?.buttonLink)
                  ? buttonProps
                  : {})}
                {...buttonAnalyticsProps}
              >
                {data[currentSlide]?.buttonText}
              </a>
            )}
          </div>
        )}

        <Carousel
          viewMode="single"
          className={styles.carousel}
          onChangeSlide={handleChangeSlide}
          activeSlide={currentSlide}
        >
          {(data ?? []).map((landingSettings, index) => (
            <div
              className={styles.content}
              key={landingSettings.id}
              {...slideAnalyticsProps}
            >
              <div
                className={cn(
                  styles["content-header"],
                  styles[`${landingSettings?.textAlignment?.toLowerCase()}`]
                )}
                style={
                  landingSettings?.background
                    ? {
                        background: landingSettings?.background,
                      }
                    : {}
                }
              >
                {!isMobile && !!landingImagesSrc[index] && (
                  <img
                    alt={landingSettings?.imageName}
                    className={styles["content-header-img"]}
                    src={landingImagesSrc[index]}
                  />
                )}
                <div className={styles["content-text"]}>
                  <h1
                    className={cn(
                      styles["content-title"],
                      commonStyles.heading,
                      commonStyles["level-2"]
                    )}
                    style={{
                      color: landingSettings?.textColor,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(landingSettings?.title ?? ""),
                    }}
                  />
                  <p
                    className={cn(
                      styles["content-subtitle"],
                      commonStyles["size-medium-long"]
                    )}
                    style={{
                      color: landingSettings?.textColor,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(landingSettings?.text ?? ""),
                    }}
                  />
                  {!!landingSettings?.buttonText?.length && (
                    <a
                      href={landingSettings?.buttonLink}
                      className={styles["content-header-button"]}
                      style={{
                        color: landingSettings?.buttonTextColor,
                        backgroundColor: landingSettings?.buttonColor,
                      }}
                      {...(isExternalReference(landingSettings?.buttonLink)
                        ? buttonProps
                        : {})}
                      onClick={handleLandingButtonClick}
                      {...buttonAnalyticsProps}
                    >
                      {landingSettings?.buttonText}
                    </a>
                  )}
                </div>
              </div>
              <div className={styles["content-blocks-wrapper"]}>
                {!!infoBlock?.length &&
                  infoBlock.map((block) => {
                    switch (block.type) {
                      case BlockTypeEnum.IMAGE_TEXT_BLOCK: {
                        return block.blockType ===
                          ImageTextBlockTypeEnum.HORIZONTAL ? (
                          <ImageTextBlockHorizontal
                            title={block.blockTitle ?? ""}
                            blocks={block.blocks}
                            key={block.id}
                          />
                        ) : (
                          <ImageTextBlockVertical
                            title={block.blockTitle ?? ""}
                            blocks={block.blocks}
                            size={
                              block.blockSize ?? ImageTextBlockSizeEnum.LITTLE
                            }
                            key={block.id}
                          />
                        );
                      }

                      case BlockTypeEnum.TEXT_BLOCK:
                        return (
                          <TextBlock
                            title={block.blockTitle ?? ""}
                            content={block.content}
                            key={block.id}
                          />
                        );

                      case BlockTypeEnum.STEPS_BLOCK:
                        return (
                          <StepsBlock
                            title={block.blockTitle ?? ""}
                            steps={block.steps}
                            key={block.id}
                          />
                        );

                      case BlockTypeEnum.TABS_BLOCK:
                        return (
                          <TabsBlock
                            title={block.blockTitle ?? ""}
                            tabs={block.tabs}
                            key={block.id}
                          />
                        );

                      default:
                        return null;
                    }
                  })}
              </div>
            </div>
          ))}
        </Carousel>
      </div>
      <Button
        type="circle"
        className={styles["close-button"]}
        onClick={handleSpecialOfferClose}
        {...closeButtonAnalyticsProps}
      >
        <CloseIcon size="small" />
      </Button>
    </Overlay>
  );
};
